import { CommonModule } from '@angular/common';
import { Component, ElementRef, QueryList, ViewChildren, ChangeDetectorRef } from '@angular/core';
import { SidebarService } from '../../services/sidebar.service';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { SafeHtmlPipe } from '../../pipe/safe-html.pipe';
import { combineLatest, Subscription } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { PermissionService, PERMISSION_NAMES } from '../../services/permission.service';

type NavItemSubItem = {
  name: string;
  path: string;
  pro?: boolean;
  new?: boolean;
  /**
   * Optional permission required to see this submenu item.
   * Uses permission name from PERMISSION_NAMES.*.
   */
  requiredPermissionName?: string;
};

type NavItem = {
  name: string;
  icon: string;
  path?: string;
  new?: boolean;
  isRootOnly?: boolean;
  /**
   * Optional permission required to see this top-level nav item.
   * Uses permission name from PERMISSION_NAMES.*.
   */
  requiredPermissionName?: string;
  subItems?: NavItemSubItem[];
};

@Component({
  selector: 'app-sidebar',
  imports: [
    CommonModule,
    RouterModule,
    SafeHtmlPipe,
  ],
  templateUrl: './app-sidebar.component.html',
})
export class AppSidebarComponent {

  // Main nav items
  navItems: NavItem[] = [
    {
      icon: `<svg width="16" height="16" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">  <path d="M1.5 1.5V9.5C1.5 9.76522 1.60536 10.0196 1.79289 10.2071C1.98043 10.3946 2.23478 10.5 2.5 10.5H10.5" stroke="#5C77FF" stroke-width="1.02083" stroke-linecap="round" stroke-linejoin="round"/>  <path d="M9 8.5V4.5" stroke="#5C77FF" stroke-width="1.02083" stroke-linecap="round" stroke-linejoin="round"/>  <path d="M6.5 8.5V2.5" stroke="#5C77FF" stroke-width="1.02083" stroke-linecap="round" stroke-linejoin="round"/>  <path d="M4 8.5V7" stroke="#5C77FF" stroke-width="1.02083" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
      name: "Dashboard",
      path: "/dashboard",
      requiredPermissionName: PERMISSION_NAMES.DASH_READ,
    },
    {
      icon: `<svg fill="none" width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
  <circle cx="12" cy="12" r="10" stroke="#5C77FF" stroke-width="2"/>
  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="#5C77FF" stroke-width="2"/>
  <path d="M2 12h20" stroke="#5C77FF" stroke-width="2"/>
</svg>`,
      name: "Domain Monitoring",
      path: "/monitor",
      requiredPermissionName: PERMISSION_NAMES.DOMAIN_VIEW,
    },
    {
      icon: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.6 3.2H11.2V1.6C11.2 0.72 10.48 0 9.6 0H6.4C5.52 0 4.8 0.72 4.8 1.6V3.2H2.4C1.04 3.2 0 4.24 0 5.6V13.6C0 14.96 1.04 16 2.4 16H13.6C14.96 16 16 14.96 16 13.6V5.6C16 4.24 14.96 3.2 13.6 3.2ZM6.4 1.6H9.6V3.2H6.4V1.6ZM14.4 13.6C14.4 14.08 14.08 14.4 13.6 14.4H2.4C1.92 14.4 1.6 14.08 1.6 13.6V8.8H14.4V13.6ZM14.4 7.2H1.6V5.6C1.6 5.12 1.92 4.8 2.4 4.8H13.6C14.08 4.8 14.4 5.12 14.4 5.6V7.2Z" fill="#5C77FF"/></svg>`,
      name: "Abuse Cases",
      path: "/abuse",
      requiredPermissionName: PERMISSION_NAMES.CASE_READ,
    },
    {
      icon: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="#5C77FF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <circle cx="8.5" cy="7" r="4" stroke="#5C77FF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M17 11a4 4 0 1 0 0-8" stroke="#5C77FF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke="#5C77FF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`,
      name: "Tenant Management",
      path: "/tenant",
      isRootOnly: true,
    },
    {
      icon: `<svg width="16" height="16" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">  <path d="M9.9375 2.25H2.0625C1.54473 2.25 1.125 2.66973 1.125 3.1875V8.8125C1.125 9.33027 1.54473 9.75 2.0625 9.75H9.9375C10.4553 9.75 10.875 9.33027 10.875 8.8125V3.1875C10.875 2.66973 10.4553 2.25 9.9375 2.25Z" stroke="#5C77FF" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>  <path d="M2.625 3.75L6 6.375L9.375 3.75" stroke="#5C77FF" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
      name: "Email Templates",
      path: "/email",
      requiredPermissionName: PERMISSION_NAMES.EMAIL_READ,
    },
    {
      icon: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" stroke="#5C77FF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" stroke="#5C77FF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`,
      name: "Configuration",
      path: "/config",
    },

  ];

  openSubmenu: string | null | number = null;
  subMenuHeights: { [key: string]: number } = {};
  @ViewChildren('subMenu') subMenuRefs!: QueryList<ElementRef>;

  readonly isExpanded$;
  readonly isMobileOpen$;
  readonly isHovered$;

  private subscription: Subscription = new Subscription();

  isRoot: boolean = false;
  canReadRoles: boolean = false;

  constructor(
    public sidebarService: SidebarService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private permissionService: PermissionService
  ) {
    this.isExpanded$ = this.sidebarService.isExpanded$;
    this.isMobileOpen$ = this.sidebarService.isMobileOpen$;
    this.isHovered$ = this.sidebarService.isHovered$;
    this.isRoot = this.authService.isRoot();
  }

  /**
   * Load user permissions for role management (uses ROLES_READ from GET /api/users/me/permissions).
   */
  loadRolePermissions(): void {
    this.permissionService.hasPermissionAsync(PERMISSION_NAMES.ROLES_READ).subscribe({
      next: (hasPermission) => {
        this.canReadRoles = hasPermission;
        this.cdr.detectChanges();
      },
      error: () => {
        this.canReadRoles = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Helper to check if current user has a given permission name.
   * If no permission name is provided, treat as allowed.
   */
  private hasPermission(permissionName?: string): boolean {
    if (!permissionName) {
      return true;
    }
    return this.permissionService.hasPermissionByName(permissionName);
  }

  /**
   * Get filtered nav items based on permissions.
   * - Filters out submenu items where the user lacks requiredPermissionName.
   * - Hides entire submenu groups when no visible sub-items remain.
   * - Optionally filters top-level items when requiredPermissionName is set.
   * Note: Resource Access Management is always visible - permissions control individual tabs within it
   */
  getFilteredNavItems(): NavItem[] {
    return this.navItems
      .map((nav) => {
        // Filter submenu items by permission (if any)
        if (nav.subItems && nav.subItems.length > 0) {
          const filteredSubItems = nav.subItems.filter((sub) =>
            this.hasPermission(sub.requiredPermissionName)
          );

          return {
            ...nav,
            subItems: filteredSubItems,
          };
        }

        return nav;
      })
      .filter((nav) => {
        // For submenu groups, keep only if at least one visible sub-item remains
        if (nav.subItems && nav.subItems.length > 0) {
          return true;
        }

        // For top-level links, apply permission and root-only rules
        if (nav.requiredPermissionName && !this.hasPermission(nav.requiredPermissionName)) {
          return false;
        }

        if (nav.isRootOnly && !this.isRoot) {
          return false;
        }

        return true;
      });
  }

  ngOnInit() {
    // Preload user permissions so sidebar filtering has data immediately
    this.permissionService.loadUserPermissions().subscribe();

    // Load role permissions
    this.loadRolePermissions();

    // Subscribe to router events
    this.subscription.add(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.setActiveMenuFromRoute(this.router.url);
        }
      })
    );

    // Subscribe to combined observables to close submenus when all are false
    this.subscription.add(
      combineLatest([this.isExpanded$, this.isMobileOpen$, this.isHovered$]).subscribe(
        ([isExpanded, isMobileOpen, isHovered]) => {
          if (!isExpanded && !isMobileOpen && !isHovered) {
            // this.openSubmenu = null;
            // this.savedSubMenuHeights = { ...this.subMenuHeights };
            // this.subMenuHeights = {};
            this.cdr.detectChanges();
          } else {
            // Restore saved heights when reopening
            // this.subMenuHeights = { ...this.savedSubMenuHeights };
            // this.cdr.detectChanges();
          }
        }
      )
    );

    // Initial load
    this.setActiveMenuFromRoute(this.router.url);
  }

  ngOnDestroy() {
    // Clean up subscriptions
    this.subscription.unsubscribe();
  }

  isActive(path: string): boolean {
    const currentUrl = this.router.url.split('?')[0]; // Remove query params
    // Exact match or current URL starts with the path (for child routes)
    // Handle root path separately to avoid matching all routes
    if (path === '/') {
      return currentUrl === '/' || currentUrl === '';
    }
    // For non-root paths, match exact or child routes
    return currentUrl === path || currentUrl.startsWith(path + '/');
  }

  toggleSubmenu(section: string, index: number) {
    const key = `${section}-${index}`;

    if (this.openSubmenu === key) {
      this.openSubmenu = null;
      this.subMenuHeights[key] = 0;
    } else {
      this.openSubmenu = key;

      setTimeout(() => {
        const el = document.getElementById(key);
        if (el) {
          this.subMenuHeights[key] = el.scrollHeight;
          this.cdr.detectChanges(); // Ensure UI updates
        }
      });
    }
  }

  onSidebarMouseEnter() {
    this.isExpanded$.subscribe(expanded => {
      if (!expanded) {
        this.sidebarService.setHovered(true);
      }
    }).unsubscribe();
  }

  private setActiveMenuFromRoute(currentUrl: string) {
    const menuGroups = [
      { items: this.navItems, prefix: 'main' },
      // { items: this.othersItems, prefix: 'others' },
    ];

    menuGroups.forEach(group => {
      group.items.forEach((nav, i) => {
        if (nav.subItems) {
          nav.subItems.forEach(subItem => {
            if (currentUrl === subItem.path) {
              const key = `${group.prefix}-${i}`;
              this.openSubmenu = key;

              setTimeout(() => {
                const el = document.getElementById(key);
                if (el) {
                  this.subMenuHeights[key] = el.scrollHeight;
                  this.cdr.detectChanges(); // Ensure UI updates
                }
              });
            }
          });
        }
      });
    });
  }

  onSubmenuClick() {
    this.isMobileOpen$.subscribe(isMobile => {
      if (isMobile) {
        this.sidebarService.setMobileOpen(false);
      }
    }).unsubscribe();
  }  

  onSignOut() {
    this.authService.logout();
    this.sidebarService.setMobileOpen(false);
  }
}
